import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { EXTENDED_DESCRIPTION_DEFAULT } from 'shared/constants';
import { Brands, ProductLookup } from 'shared/types';

export function useSelectFormProduct(index?: number) {
  const { brand } = useParams<{ brand: Brands }>();
  const { setValue } = useFormContext();

  const updateField = useCallback(
    (field: `${string}`, value: any) => {
      const prefix = `products.${index}`;
      const key = `${prefix}.${field}` as const;
      setValue(index !== undefined ? key : field, value);
    },
    [index, setValue]
  );
  const onChange = useCallback(
    (product: string | Record<keyof ProductLookup, string> | null) => {
      if (typeof product === 'string') {
      } else if (product && product.inputValue) {
        // This is a new product
        const freightadderValue = brand === 'cbe' ? 0.0375 : '';
        updateField('isNew', true);
        updateField('noType', true);
        updateField('outOfScope', false);
        updateField('name', product?.inputValue || '');
        updateField('itemDescriptionAdj', product?.itemdescriptionadj || '');
        updateField('itemId', product?.itemid || '');
        updateField('concatenatedItem', product?.concatenateditem || '');
        updateField('productType', product?.producttype || '');
        updateField('vendorName', product?.vendorname || '');
        updateField('vendorId', product?.vendorid || '');
        updateField('lastCostPaid', Number(product?.lastcostpaid));
        updateField('listPrice', Number(product?.listprice));
        updateField('marginAtList', Number(product?.marginatlist));
        updateField('marginPercentileEighty', Number(product?.marginpercentileeighty));
        updateField('costDate', product?.costdate);
        updateField('quantityBreakFlag', product?.quantitybreakflag);
        updateField('costEffectiveDate', product?.costeffectivedate);
        updateField('listPriceEffectiveDate', product?.listpriceeffectivedate);
        updateField('unitOfMeasure', product?.unitofmeasure);
        updateField('factorSheetName', product?.factorsheetname);
        updateField('MROvsCapital', product?.mrovscapital);
        updateField('freightadder', freightadderValue);
      } else {
        updateField('outOfScope', product?.outofscope === 'OUT OF SCOPE');
        updateField('isNew', false);
        updateField('name', product?.itemdescription || '');
        updateField('itemDescriptionAdj', product?.itemdescriptionadj || '');
        updateField('itemId', product?.itemid || '');
        updateField('concatenatedItem', product?.concatenateditem || '');
        updateField('vendorName', product?.vendorname || '');
        updateField('vendorId', product?.vendorid || '');
        updateField('listPrice', Number(product?.listprice));
        updateField('marginAtList', Number(product?.marginatlist));
        updateField('marginPercentileEighty', Number(product?.marginpercentileeighty));
        updateField('costDate', product?.costdate);
        updateField('quantityBreakFlag', (product?.quantitybreakflag || '') === 'TRUE');
        updateField('extendedDesc', product?.extendeddesc || EXTENDED_DESCRIPTION_DEFAULT);
        updateField('costEffectiveDate', product?.costeffectivedate || '');
        updateField('listPriceEffectiveDate', product?.listpriceeffectivedate || '');
        updateField('unitOfMeasure', product?.unitofmeasure || '');
        updateField('factorSheetName', product?.factorsheetname || '');
        updateField('MROvsCapital', product?.mrovscapital || '');
        updateField('exchangerate', product?.exchangerate || '');
        updateField('freightadder', product?.freightadder || '');
        updateField('currencytype', product?.currencytype || '');
        if (product?.lastcostpaid) {
          updateField('lastCostPaid', Number(product?.lastcostpaid));
        } else {
          updateField('lastCostPaid', 0);
        }
        if (product?.producttype) {
          if (product?.producttype === 'NOT PROVIDED') {
            updateField('noType', true);
            updateField('productType', '');
          } else {
            updateField('productType', product?.producttype);
            updateField('noType', false);
          }
        } else {
          updateField('productType', '');
          updateField('noType', true);
        }
      }
    },
    [updateField, brand]
  );
  const onClear = useCallback(() => {
    updateField('itemId', '');
    updateField('productType', '');
    updateField('vendorName', '');
    updateField('vendorId', '');
    updateField('lastCostPaid', '');
    updateField('listPrice', '');
    updateField('marginAtList', '');
    updateField('noType', false);
    updateField('isNew', false);
    updateField('exchangerate', '');
    updateField('freightadder', '');
    updateField('currencytype', '');
  }, [updateField]);

  return [onChange, onClear] as const;
}
